import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import * as React from 'react';
import { supportRef, composeRef } from "rc-util/es/ref"; // import raf from './raf';

import { ConfigConsumer, ConfigContext } from '../ConfigProvider';
import { cloneElement } from './reactNode';
var styleForPseudo;

function isHidden(element) {
  return !element || element.offsetParent === null || element.hidden;
}

function isNotGrey(color) {
  // eslint-disable-next-line no-useless-escape
  var match = (color || '').match(/rgba?\((\d*), (\d*), (\d*)(, [\d.]*)?\)/);

  if (match && match[1] && match[2] && match[3]) {
    return !(match[1] === match[2] && match[2] === match[3]);
  }

  return true;
}

var Wave = function Wave(props) {
  var children = props.children,
      insertExtraNode = props.insertExtraNode;

  var _React$useContext = React.useContext(ConfigContext),
      getPrettyCls = _React$useContext.getPrettyCls;

  var preflx = getPrettyCls('')();
  var containerRef = /*#__PURE__*/React.createRef();
  var isMountedRef = React.useRef(false);

  var _React$useState = React.useState(-1),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      clickWaveTimeoutId = _React$useState2[0],
      setClickWaveTimeoutId = _React$useState2[1]; // const [animationStartId, setAnimationStartId] = React.useState(-1);


  var _React$useState3 = React.useState(false),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      animationStart = _React$useState4[0],
      setAnimationStart = _React$useState4[1];

  var _React$useState5 = React.useState(true),
      _React$useState6 = _slicedToArray(_React$useState5, 2),
      firstClick = _React$useState6[0],
      setFirstClick = _React$useState6[1];

  React.useEffect(function () {
    isMountedRef.current = true;
    return function () {
      isMountedRef.current = false;
    };
  }, []);
  React.useEffect(function () {
    var node = containerRef.current;

    var getAttributeName = function getAttributeName() {
      return insertExtraNode ? "".concat(preflx, "-click-animating") : "".concat(preflx, "-click-animating-without-extra-node");
    };

    var resetEffect = function resetEffect(node) {
      if (!node || !(node instanceof Element)) {
        return;
      }

      var attributeName = getAttributeName();
      node.setAttribute(attributeName, 'false');

      if (styleForPseudo) {
        styleForPseudo.innerHTML = '';
      }

      ['transition', 'animation'].forEach(function (name) {
        node.removeEventListener("".concat(name, "start"), onTransitionStart);
        node.removeEventListener("".concat(name, "end"), onTransitionEnd);
      });
    };

    var onTransitionStart = function onTransitionStart(e) {
      if (!e || e.target !== node || animationStart || firstClick) {
        return;
      }

      resetEffect(node);
      setFirstClick(false);
    };

    var onTransitionEnd = function onTransitionEnd(e) {
      if (!e || e.animationName !== 'waveEffect' && e.animationName !== 'fadeEffect') {
        return;
      }

      resetEffect(e.target);

      if (isMountedRef.current) {
        setAnimationStart(false);
      }
    };

    var handleClick = function handleClick(node, waveColor) {
      if (!node || isHidden(node) || node.className.indexOf('-leave') >= 0) {
        return;
      }

      var attributeName = getAttributeName();
      node.setAttribute(attributeName, 'true'); // generate waveShadowColor when the element

      styleForPseudo = styleForPseudo || document.createElement('style');

      if (waveColor && waveColor !== '#ffffff' && waveColor !== 'rgb(255, 255, 255)' && isNotGrey(waveColor) && !/rgba\((?:\d*, ){3}0\)/.test(waveColor) && // any transparent rgba color
      waveColor !== 'transparent') {
        styleForPseudo.innerHTML = "\n        [".concat(preflx, "-click-animating-without-extra-node='true']::after, .").concat(preflx, "-click-animating-node {\n          --wave-shadow-color: ").concat(waveColor, ";\n        }");

        if (!node.ownerDocument.body.contains(styleForPseudo)) {
          node.ownerDocument.body.appendChild(styleForPseudo);
        }
      }

      ['transition', 'animation'].forEach(function (name) {
        node.addEventListener("".concat(name, "start"), onTransitionStart);
        node.addEventListener("".concat(name, "end"), onTransitionEnd);
        setTimeout(function () {
          onTransitionEnd({
            target: node,
            animationName: 'fadeEffect'
          });
        }, 300);
      });
    };

    var onClick = function onClick(e) {
      if (e.target.tagName === 'INPUT' || isHidden(e.target)) {
        return;
      }

      resetEffect(node);
      var waveColor = getComputedStyle(node).getPropertyValue('border-top-color') || // Firefox Compatible
      getComputedStyle(node).getPropertyValue('border-color') || getComputedStyle(node).getPropertyValue('background-color');
      clearTimeout(clickWaveTimeoutId);
      var cId = window.setTimeout(function () {
        return handleClick(node, waveColor);
      }, 0);
      setClickWaveTimeoutId(cId);
      handleClick(node, waveColor); // raf.cancel(animationStartId);

      setAnimationStart(true); // TODO: fix the re-render
      // const aId = raf(() => {
      //   setAnimationStart(false);
      // }, 10);
      // setAnimationStartId(aId);
    };

    var bindAnimationEvent = function bindAnimationEvent(node) {
      if (!node || !node.getAttribute || node.getAttribute('disabled') || node.className.indexOf('disabled') >= 0) {
        return {
          cancel: function cancel() {}
        };
      }

      node.addEventListener('click', onClick, true); // eslint-disable-next-line consistent-return

      return {
        cancel: function cancel() {
          node.removeEventListener('click', onClick, true);
        }
      };
    };

    if (node && node.nodeType === 1) {
      var instance = bindAnimationEvent(node);
      return function () {
        // console.log('clickTimeout', clickWaveTimeoutId, containerRef);
        instance.cancel();
        clearTimeout(clickWaveTimeoutId);
      };
    }

    return function () {
      clearTimeout(clickWaveTimeoutId);
    };
  }, [animationStart, clickWaveTimeoutId, containerRef, firstClick, insertExtraNode, preflx]);

  var renderWave = function renderWave() {
    if (! /*#__PURE__*/React.isValidElement(children)) return children;
    var ref = containerRef;

    if (supportRef(children)) {
      ref = composeRef(children.ref, containerRef);
    }

    return cloneElement(children, {
      ref: ref
    });
  };

  return /*#__PURE__*/React.createElement(ConfigConsumer, null, renderWave);
};

export default Wave;